/**
 * ============================================
 * Authentication Utilities
 * ============================================
 * 토큰/유저정보는 in-memory 클로저 변수로 보관 — sessionStorage 미사용.
 *  - XSS 시 토큰 탈취 위험 최소화
 *  - webview 종료 시 자동 소멸 (RN unmount → 모듈 평가 다시 → 변수 초기화)
 *
 * 자동 로그인 설정만 localStorage에 보관 — 사용자 환경 설정으로 webview 재진입 시 유지 필요.
 */

// ============================================
// In-memory Token / UserInfo Storage
// ============================================
let _accessToken: string | null = null;
let _refreshToken: string | null = null;
let _userInfo: { userId?: string; email?: string; nickname?: string } = {};

const AUTO_LOGIN_KEY = "isAutoLogin";

// ============================================
// JWT Utility (모듈 내부 전용)
// ============================================
function parseJwt(token: string): Record<string, any> | null {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error('Failed to parse JWT:', e);
        return null;
    }
}

/** 토큰 만료 여부 체크 */
export function isTokenExpired(token: string | null): boolean {
    if (!token) return true;
    const payload = parseJwt(token);
    if (!payload || !payload.exp) return true;
    return Date.now() >= payload.exp * 1000;
}

// ============================================
// Token API
// ============================================
export function setTokens(accessToken: string, refreshToken?: string): void {
    _accessToken = accessToken;
    if (refreshToken !== undefined) {
        _refreshToken = refreshToken;
    }
}

export function getAccessToken(): string | null {
    return _accessToken;
}

export function getRefreshToken(): string | null {
    return _refreshToken;
}

// ============================================
// UserInfo API
// ============================================
export function setUserInfo(userInfo: {
    userId?: string;
    email?: string;
    nickname?: string;
}): void {
    if (userInfo.userId !== undefined) _userInfo.userId = userInfo.userId;
    if (userInfo.email !== undefined) _userInfo.email = userInfo.email;
    if (userInfo.nickname !== undefined) _userInfo.nickname = userInfo.nickname;
}

// ============================================
// Auto Login (localStorage — 환경 설정 유지 필요)
// ============================================
export function setAutoLogin(isAutoLogin: boolean): void {
    localStorage.setItem(AUTO_LOGIN_KEY, isAutoLogin ? 'Y' : 'N');
}

// ============================================
// Clear All (logout / withdraw / cancel)
// ============================================
export function clearAuth(): void {
    _accessToken = null;
    _refreshToken = null;
    _userInfo = {};
    // 자동 로그인 설정은 유지 (localStorage)
}
